import { unstable_cache } from 'next/cache';
import { cache } from 'react';

import { api } from '@/lib/api';
import { CHARACTERS_HUB_REVALIDATE_SECONDS } from '../consts/SEO_CACHE';

const CATALOG_PAGE_SIZE = 20;

export interface FandomCharactersHubItemSSR {
  id: string | number;
  name?: string;
  description?: string | null;
  slug?: string | null;
  permanentUrl?: string | null;
  fanfic_cnt?: number;
  image?: { id: number; url: string | null } | null;
}

export interface FandomCharactersHubDataSSR {
  characters: FandomCharactersHubItemSSR[];
  charactersCount: number;
  totalPages: number;
}

interface GetFandomCharactersHubParamsSSR {
  fandomId: string;
  page: number;
  searchQuery: string;
}

/**
 * Cached characters hub listing for a fandom. Public data — safe to share across requests.
 */
export const getFandomCharactersHubReqSSR = cache(
  async ({
    fandomId,
    page,
    searchQuery,
  }: GetFandomCharactersHubParamsSSR): Promise<FandomCharactersHubDataSSR> => {
    const normalizedSearch = searchQuery.trim();

    return unstable_cache(
      async () => {
        const charactersQuery = new URLSearchParams();
        charactersQuery.set('fandomId', fandomId);
        charactersQuery.set('page', String(page));
        charactersQuery.set('limit', String(CATALOG_PAGE_SIZE));
        charactersQuery.set('minFanfics', '1');
        if (normalizedSearch) charactersQuery.set('search', normalizedSearch);

        try {
          const response = await api.get(`/api/characters-custom?${charactersQuery.toString()}`);
          const characters = (response.data?.characters ?? []) as FandomCharactersHubItemSSR[];
          const charactersCount =
            response.data?.total ?? response.data?.totalDocs ?? characters.length;
          const totalPages = response.data?.totalPages ?? 1;

          return {
            characters,
            charactersCount,
            totalPages,
          };
        } catch (error) {
          console.error('[SSR] Failed to load fandom characters hub:', error);
          return {
            characters: [],
            charactersCount: 0,
            totalPages: 1,
          };
        }
      },
      ['fandom-characters-hub', fandomId, String(page), normalizedSearch],
      { revalidate: CHARACTERS_HUB_REVALIDATE_SECONDS },
    )();
  },
);
